import axios from 'axios';
import { baseURL } from '../Constants';

export interface SeasonSettings {
  id: number;
  season: number;
  groupsVisible: boolean;
  announcementDate: string | null;
  createdAt: string;
  updatedAt: string;
  updatedBy: string | null;
}

export interface UpdateSeasonSettingsRequest {
  groupsVisible?: boolean;
  announcementDate?: string | null;
}

export interface GroupsLockedResponse {
  season: number;
  groupsVisible: boolean;
  message: string;
  announcementDate: string | null;
  teamsCount: number;
}

class SeasonSettingsService {
  /**
   * Get season settings for a specific season (PUBLIC)
   */
  async getSeasonSettings(season: number): Promise<SeasonSettings> {
    try {
      const response = await axios.get(
        `${baseURL}/SeasonSettings/${season}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching season settings for season ${season}:`, error);
      throw error;
    }
  }

  /**
   * Get all season settings (ADMIN ONLY)
   */
  async getAllSeasonSettings(authToken: string): Promise<SeasonSettings[]> {
    try {
      const response = await axios.get(
        `${baseURL}/SeasonSettings/all`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching all season settings:', error);
      throw error;
    }
  }

  /**
   * Update season settings (ADMIN ONLY)
   */
  async updateSeasonSettings(season: number, request: UpdateSeasonSettingsRequest, authToken: string): Promise<{ message: string; settings: SeasonSettings }> {
    try {
      const response = await axios.put(
        `${baseURL}/SeasonSettings/${season}`,
        request,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating season settings for season ${season}:`, error);
      throw error;
    }
  }

  /**
   * Quick toggle for groups visibility (ADMIN ONLY)
   */
  async toggleGroupsVisibility(season: number, visible: boolean, authToken: string): Promise<{ message: string; settings: SeasonSettings }> {
    try {
      const response = await axios.patch(
        `${baseURL}/SeasonSettings/${season}/toggle`,
        visible,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Error toggling groups visibility for season ${season}:`, error);
      throw error;
    }
  }

  /**
   * Set announcement date (ADMIN ONLY)
   */
  async setAnnouncementDate(season: number, announcementDate: string | null, authToken: string): Promise<{ message: string; settings: SeasonSettings }> {
    try {
      const response = await axios.patch(
        `${baseURL}/SeasonSettings/${season}/announcement`,
        announcementDate,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Error setting announcement date for season ${season}:`, error);
      throw error;
    }
  }
}

export const seasonSettingsService = new SeasonSettingsService();
